import {
  type ReadableStore,
  type Store,
  createStore,
  deriveStore,
} from '@frontend/viewer-core';

/**
 * Not generic over the recorded state, deliberately: nothing on this surface hands
 * a snapshot out or takes one in. Consumers step the stack and read whether they
 * can; only `createHistoryStore` needs to know what is being recorded.
 */
export type HistoryStore = {
  /** True when there is an earlier state to go back to. */
  readonly canUndo: ReadableStore<boolean>;
  /** True when an undo has been made and not yet re-applied. */
  readonly canRedo: ReadableStore<boolean>;
  undo(): void;
  redo(): void;
  /**
   * Discards the stack and re-seeds from whatever the source holds now.
   *
   * Called at every boundary a person would read as a fresh start: entering or
   * leaving mapping mode, a successful save, a new drawing.
   */
  reset(): void;
  /**
   * Applies a change that is not its own undo step.
   *
   * The change still happens, and it *stays* happened — the current entry is
   * rebased onto the result rather than left behind it. That distinction is the
   * whole point: hiding a layer prunes the selection, and while the user must not
   * lose that pruning, neither should a later undo resurrect a selection pointing
   * at geometry they have since hidden.
   */
  withoutRecording(fn: () => void): void;
  /** Stops recording. Call when the session is disposed. */
  detach(): void;
};

type Options = {
  /**
   * How many states to keep, including the current one.
   *
   * Entries are two object references apiece and unchanged halves are shared, so
   * the cost is dominated by how many *distinct* large selections a user builds in
   * one session.
   */
  capacity: number;
};

/**
 * Undo/redo over a single store, recorded by subscription.
 *
 * Recording is driven by watching the source rather than by explicit `commit()`
 * calls at each edit site, because a selection can change from at least six
 * places — a marquee release, a click on empty canvas, a shift-click, a
 * double-click drill, the layers panel, the mapping panel — and a stack that
 * depends on every one of them remembering to check in is a stack with holes in
 * it. The exceptions are few enough to enumerate, and they say so at the call
 * site via `withoutRecording`.
 *
 * Semantics follow `@frontend/utils`'s `useStateWithHistory`, which the BOM
 * builder already uses, so the two behave alike: a new edit made after an undo
 * discards the redo tail, and reaching capacity drops the oldest entry.
 *
 * Deliberately generic over `T`. What the DXF viewer records is a *composite* of
 * selection and mapping — see `createMappingSnapshotSlice` — and this file has no
 * business knowing that.
 */
export const createHistoryStore = <T>(
  source: Store<T>,
  { capacity }: Options,
): HistoryStore => {
  if (capacity < 2) {
    // One entry is the present, so a stack that can undo anything needs two.
    throw new Error(`History capacity must be at least 2, got '${capacity}'`);
  }

  let entries: T[] = [source.getState()];
  let position = 0;
  let isRecording = true;

  /**
   * Position and length, published so `canUndo` / `canRedo` can be subscribed to
   * from React. The entries themselves are not on a store: nothing renders them,
   * and putting an array of snapshots behind a notification would invite exactly
   * the whole-page re-renders the session exists to avoid.
   */
  const cursor: Store<{ position: number; length: number }> = createStore({
    position: 0,
    length: 1,
  });

  const publish = () =>
    cursor.setState((previous) =>
      previous.position === position && previous.length === entries.length
        ? previous
        : { position, length: entries.length },
    );

  /** Moves the source without the move being recorded as a new step. */
  const replay = (next: T) => {
    isRecording = false;
    try {
      source.setState(() => next);
    } finally {
      isRecording = true;
    }
  };

  const unsubscribe = source.subscribe(() => {
    if (!isRecording) {
      return;
    }
    const next = source.getState();
    if (Object.is(next, entries[position])) {
      return;
    }

    // An edit after an undo forks: the states that were ahead are unreachable and
    // must not come back via redo.
    if (position < entries.length - 1) {
      entries = entries.slice(0, position + 1);
    }

    entries.push(next);
    position = entries.length - 1;

    if (entries.length > capacity) {
      entries = entries.slice(entries.length - capacity);
      position = entries.length - 1;
    }

    publish();
  });

  return {
    canUndo: deriveStore(cursor, (current) => current.position > 0),
    canRedo: deriveStore(
      cursor,
      (current) => current.position < current.length - 1,
    ),

    undo() {
      if (position === 0) {
        return;
      }
      position -= 1;
      replay(entries[position]);
      publish();
    },

    redo() {
      if (position >= entries.length - 1) {
        return;
      }
      position += 1;
      replay(entries[position]);
      publish();
    },

    reset() {
      entries = [source.getState()];
      position = 0;
      publish();
    },

    withoutRecording(fn) {
      const wasRecording = isRecording;
      isRecording = false;
      try {
        fn();
      } finally {
        isRecording = wasRecording;
      }
      // Rebase rather than leave the entry stale, so the change is kept but is not
      // something an undo can walk back behind. Skipped when already suppressed by
      // an outer call, which owns the rebase for the whole nested region.
      if (wasRecording) {
        entries[position] = source.getState();
      }
    },

    detach: unsubscribe,
  };
};
